/**
 * Product suggestions — response shape for `GET /api/suggestions/products`
 * (T12.5 — the "Suggested · likely needed soon" shortlist on the Add-to-list
 * sheet).
 *
 * Mirrors the backend `ProductSuggestion` shape (backend/src/types) verbatim —
 * snake_case end-to-end, no transform (match the `/api/flyerdeals` precedent).
 * The endpoint returns the already-ranked list; the client renders it as-is.
 *
 * T12.5 surfaces only `product_name` + `reason_text` as a plain row. The
 * remaining `suggested_*` / `days_*` fields are optional and carried for the
 * comprehensive engine's richer rows (enrichment lands in T44.6); they are not
 * read here.
 */

import { z } from 'zod';

// The four reason kinds the backend emits: frequency_due | always_stocked |
// seasonal | search_match. Modeled as a union of literals (mirrors the backend
// `SuggestionReason` string union).
export const suggestionReasonSchema = z.union([
  z.literal('frequency_due'),
  z.literal('always_stocked'),
  z.literal('seasonal'),
  z.literal('search_match'),
]);
export type SuggestionReason = z.infer<typeof suggestionReasonSchema>;

export const productSuggestionSchema = z.object({
  householdproduct_id: z.string(),
  product_name: z.string(),
  // Backend builds this from a LEFT JOIN on seedCategories.name — serializes to
  // JSON as `null` when the product has no category, so accept null AND
  // undefined (`.nullish()`), not just optional. See T12.5 follow-up.
  category_name: z.string().nullish(),
  reason: suggestionReasonSchema,
  reason_text: z.string(),
  score: z.number(),
  // Enrichment fields (T44.6) — not rendered by T12.5. These derive from an
  // optional purchase cadence (mostcommonunit / mostfrequentstorelocation_*),
  // which serialize to JSON as `null` when absent — `.nullish()`, not optional.
  suggested_quantity: z.number().optional(),
  suggested_unit: z.string().nullish(),
  suggested_store_id: z.string().nullish(),
  suggested_store_name: z.string().nullish(),
  days_since_last_purchase: z.number().nullish(),
  days_overdue: z.number().nullish(),
});
export type ProductSuggestion = z.infer<typeof productSuggestionSchema>;

export const suggestionsResponseSchema = z.object({
  suggestions: z.array(productSuggestionSchema),
});
export type SuggestionsResponse = z.infer<typeof suggestionsResponseSchema>;
